"use client";

import type { Map as MLMap } from "maplibre-gl";
import { useState } from "react";
import {
  Button,
  Card,
  Icon,
  Input,
  Map as MapGlyph,
  Spinner,
  TriangleAlert,
  cx,
} from "@/components";
import type { ColumnMapping, GeocodeMode, ParsedRow, RowCeilingResult } from "@/lib/ingest";
import type { MapSpec, PointSet, TileDescriptor } from "@/lib/map";
import { REASON_LABELS, REASON_HINTS } from "./labels";
import {
  hasUsableLocationMapping,
  remediationFieldError,
  remediationFields,
} from "./remediation";
import { MapCanvas, type MapView, type OverlayRenderData } from "../map/MapCanvas";

/** Keep the list light on huge files; the heading count tells the whole story. */
const MAX_LISTED = 200;

export interface MapPlateProps {
  mode: GeocodeMode;
  ready: number;
  setAside: number;
  /** Blank/summary rows dropped automatically — shown only as a quiet note. */
  skipped: number;
  /** True while an address batch is off being geocoded. */
  geocoding: boolean;
  /**
   * The last "Make map" attempt resolved zero placeable points (MAP-128). Surfaces
   * the blocking reason ON the plate — where the user is looking — instead of only
   * as a fleeting toast + the attention tray below the fold in the side column.
   */
  placementFailed?: boolean;
  /**
   * MAP-131 — remediation moved ONTO the plate. When rows need attention the
   * empty-map real estate becomes the place to fix them (reason + row preview +
   * removal), so the map renders here once they're resolved — instead of
   * a separate "N rows need attention" tray buried below the fold under Make map.
   */
  rows: ParsedRow[];
  excluded: ReadonlySet<number>;
  rowCeiling: RowCeilingResult;
  onRemoveBrokenRow: (index: number) => void;
  /** Present while ingesting; saved maps reopen from their render snapshot without it. */
  mapping: ColumnMapping | null;
  onRemediateRow: (index: number, edits: ReadonlyArray<{ column: number; value: string }>) => void;
  /** The live map, once "Make map" has produced it. */
  live: { pointSet: PointSet; spec: MapSpec } | null;
  /** One-row, unsaved mapping preview shown before the full map is made. */
  preview?: { pointSet: PointSet; spec: MapSpec; label: string } | null;
  tile: TileDescriptor;
  /** Esri satellite descriptor (MAP-102). */
  satelliteTile?: TileDescriptor;
  themeName: string;
  onViewChange?: (view: MapView) => void;
  /** M5 highlight seam — row ids a QUERY answer lights up on the canvas. */
  highlightIds?: string[];
  /** M5 annotation-badge seam — on-map labels (group-by aggregates, distance-to). */
  annotations?: Array<{ id: string; text: string }>;
  /** M5 fit seam — frame the matched features (QUERY `fit` effect). */
  fitRequest?: { ids: string[]; nonce: number } | null;
  /** M6 seam — the live MapLibre map, for PNG export capture. */
  onMapReady?: (map: MLMap) => void;
  /** M8 draw seam — active analysis draw mode (suspends panning while set). */
  analysisDraw?: "lasso" | "radius" | null;
  /** M8 draw seam — fires with the completed radius/lasso geometry. */
  onAnalysisDraw?: (
    result:
      | { kind: "lasso"; ring: [number, number][] }
      | { kind: "radius"; center: [number, number]; radiusMeters: number },
  ) => void;
  /** M9 resolved choropleth data (boundaries + values + classification), or null. */
  overlayData?: OverlayRenderData | null;
  /** M9 seam — fires with a boundary id when the choropleth is clicked. */
  onBoundaryClick?: (boundaryId: string) => void;
}

/**
 * The map plate — the hero. A framed atlas plate (elevation.frame + double
 * hairline) on muted cool chart paper. Holds the quiet placeholder until
 * "Make map" produces a PointSet + MapSpec, then mounts the real MapLibre
 * canvas INSIDE the same frame — chrome sits around the map, never over it.
 *
 * When rows need attention (MAP-131) the same real estate becomes a remediation
 * workspace: the blocking reason + the fixable rows, right where the user looks.
 */
export function MapPlate({
  mode,
  ready,
  setAside,
  skipped,
  geocoding,
  placementFailed = false,
  rows,
  excluded,
  rowCeiling,
  onRemoveBrokenRow,
  mapping,
  onRemediateRow,
  live,
  preview = null,
  tile,
  satelliteTile,
  themeName,
  onViewChange,
  highlightIds,
  annotations,
  fitRequest,
  onMapReady,
  analysisDraw,
  onAnalysisDraw,
  overlayData,
  onBoundaryClick,
}: MapPlateProps) {
  const [remedyOpen, setRemedyOpen] = useState(false);
  const modeLabel = mode === "coordinate" ? "coordinate" : "address";

  // Rows the user still has to deal with. Removed rows disappear from remediation.
  const attention = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row, index }) => row.status === "needs_attention" && !excluded.has(index));
  const attentionCount = attention.length;
  const hasLocationMapping = mapping ? hasUsableLocationMapping(mapping) : live !== null;
  const firstEditableIndex =
    mapping
      ? attention.find(
          ({ row, index }) =>
            !excluded.has(index) &&
            row.attention_reason !== undefined &&
            remediationFields(mapping, row.attention_reason).length > 0,
        )?.index ?? null
      : null;
  const ceiling =
    rowCeiling.level !== "ok" && rowCeiling.message !== undefined ? rowCeiling : null;

  // A missing/insufficient column mapping is one dataset-wide problem, not one
  // broken-row problem repeated for every row. Keep that state on the column
  // matcher. Row remediation appears only after the location structure is usable.
  // With usable points, the map remains the primary result and remediation opens
  // explicitly from its notice.
  const showRemedy =
    !geocoding && hasLocationMapping && attentionCount > 0 && (ready === 0 || remedyOpen);
  const blocked =
    !showRemedy && hasLocationMapping && (placementFailed || (ready === 0 && setAside > 0))
      ? {
          title: placementFailed ? "No rows could be placed" : "No rows are ready to place",
          note: placementFailed
            ? "None of your rows produced a usable location. Check the column match beside, then try again."
            : "Check the column match beside, then press Generate map.",
        }
      : null;

  // Blank/summary rows are dropped automatically — mentioned only as a quiet,
  // reassuring aside on the count line, never as a problem to act on.
  const skippedSuffix =
    skipped > 0
      ? ` · ${skipped.toLocaleString()} blank ${skipped === 1 ? "row" : "rows"} skipped`
      : "";

  return (
    <Card framed className="ingest-plate" data-map-plate>
      {live && !showRemedy && attentionCount > 0 ? (
        <div className="ingest-plate__attention-notice" role="status">
          <Icon icon={TriangleAlert} size="sm" />
          <span>
            We were unable to map {attentionCount.toLocaleString()} data {attentionCount === 1 ? "point" : "points"}
          </span>
          <Button variant="destructive" size="sm" onClick={() => setRemedyOpen(true)}>
            Fix {attentionCount === 1 ? "it" : "them"}
          </Button>
        </div>
      ) : null}
      <div className="ingest-plate__canvas" data-blocked={showRemedy || blocked ? "" : undefined}>
        {live && !showRemedy ? (
          <>
            <MapCanvas
              pointSet={live.pointSet}
              spec={live.spec}
              mode="editor"
              tile={tile}
              satelliteTile={satelliteTile}
              themeName={themeName}
              onViewChange={onViewChange}
              highlightIds={highlightIds}
              annotations={annotations}
              fitRequest={fitRequest}
              onReady={onMapReady}
              analysisDraw={analysisDraw}
              onAnalysisDraw={onAnalysisDraw}
              overlayData={overlayData}
              onBoundaryClick={onBoundaryClick}
            />
          </>
        ) : geocoding ? (
          <div className="ingest-plate__center" key="geocoding">
            <Spinner size="lg" className="ingest-plate__glyph" />
            <p className="ingest-plate__title">Building your map</p>
            <p className="ingest-plate__note">Crunching your map locations now</p>
            <p className="ingest-plate__count">
              Processing {ready.toLocaleString()} {ready === 1 ? "row" : "rows"}
            </p>
          </div>
        ) : preview && !showRemedy ? (
          <>
            <MapCanvas
              pointSet={preview.pointSet}
              spec={preview.spec}
              mode="editor"
              tile={tile}
              satelliteTile={satelliteTile}
              themeName={themeName}
              onViewChange={onViewChange}
              onReady={onMapReady}
            />
            <div className="ingest-plate__preview-label">
              <strong>Preview</strong>
              <span>{preview.label}</span>
            </div>
          </>
        ) : showRemedy ? (
          <div className="ingest-plate__remedy" key="remedy" aria-label="Rows that need attention">
            <div className="ingest-plate__remedy-head">
              <Icon icon={TriangleAlert} size="lg" className="ingest-plate__remedy-glyph" />
              <div className="ingest-plate__remedy-heading">
                <p className="ingest-plate__remedy-title">
                  {ready === 0
                    ? "We need help building your map"
                    : `${attentionCount.toLocaleString()} ${attentionCount === 1 ? "row needs" : "rows need"} attention`}
                </p>
                <span className="ingest-plate__remedy-count">
                  {attentionCount.toLocaleString()} {attentionCount === 1 ? "error" : "errors"} to fix
                </span>
              </div>
            </div>
            {ceiling && (
              <div
                className={cx(
                  "ingest-plate__remedy-ceiling",
                  `ingest-plate__remedy-ceiling--${ceiling.level}`,
                )}
              >
                {ceiling.message}
              </div>
            )}
            <ul className="ingest-plate__remedy-list">
              {attention.slice(0, MAX_LISTED).map(({ row, index }) => {
                const reason =
                  row.status === "needs_attention" && row.attention_reason
                    ? REASON_LABELS[row.attention_reason]
                    : "This row needs attention";
                const hint =
                  row.status === "needs_attention" && row.attention_reason
                    ? REASON_HINTS[row.attention_reason]
                    : null;
                const fields = mapping && row.attention_reason
                  ? remediationFields(mapping, row.attention_reason)
                  : [];
                const isEditing = firstEditableIndex === index;
                return (
                  <li key={index} className="ingest-remedy-row">
                    <div className="ingest-remedy-row__body">
                      <span className="ingest-remedy-row__reason">{reason}</span>
                      {hint && !isEditing && <span className="ingest-remedy-row__hint">{hint}</span>}
                      {isEditing ? (
                        <form
                          className="ingest-remedy-form"
                          onSubmit={(event) => {
                            event.preventDefault();
                            const form = new FormData(event.currentTarget);
                            const edits = fields.map((field) => ({
                              column: field.column,
                              value: String(form.get(`column-${field.column}`) ?? ""),
                            }));
                            const attentionReason = row.attention_reason;
                            const hasInvalidEdit =
                              attentionReason !== undefined &&
                              fields.some((field) => {
                                const edit = edits.find(({ column }) => column === field.column);
                                return remediationFieldError(
                                  attentionReason,
                                  field,
                                  edit?.value ?? "",
                                ) !== null;
                              });
                            if (hasInvalidEdit) return;
                            onRemediateRow(index, edits);
                            if (live) setRemedyOpen(false);
                          }}
                        >
                          {fields.map((field, fieldIndex) => {
                            const currentValue = row.cells[field.column] ?? "";
                            const fieldError = row.attention_reason
                              ? remediationFieldError(row.attention_reason, field, currentValue)
                              : null;
                            return (
                              <label key={field.column} className="ingest-remedy-form__field">
                                <span>{field.label}</span>
                                <Input
                                  name={`column-${field.column}`}
                                  defaultValue={currentValue}
                                  placeholder={field.placeholder}
                                  required
                                  autoFocus={fieldIndex === 0}
                                  error={fieldError !== null}
                                  aria-describedby={fieldError ? `row-${index}-column-${field.column}-error` : undefined}
                                />
                                {fieldError ? (
                                  <span
                                    id={`row-${index}-column-${field.column}-error`}
                                    className="ingest-remedy-form__error"
                                  >
                                    {fieldError}
                                  </span>
                                ) : null}
                              </label>
                            );
                          })}
                          <div className="ingest-remedy-form__actions">
                            <Button type="submit">Save fix</Button>
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => {
                                onRemoveBrokenRow(index);
                                if (live) setRemedyOpen(false);
                              }}
                            >
                              Remove broken row
                            </Button>
                          </div>
                        </form>
                      ) : null}
                    </div>
                    {!isEditing ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          onRemoveBrokenRow(index);
                          if (live) setRemedyOpen(false);
                        }}
                      >
                        Remove broken row
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {attention.length > MAX_LISTED && (
              <p className="ingest-plate__remedy-more">
                …and {(attention.length - MAX_LISTED).toLocaleString()} more. Fixing the file
                itself is fastest at this scale.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="ingest-plate__center" key={blocked ? "blocked" : "pending"}>
              {blocked ? (
                <>
                  <Icon
                    icon={TriangleAlert}
                    size="xl"
                    className="ingest-plate__glyph ingest-plate__glyph--alert"
                  />
                  <p className="ingest-plate__title">{blocked.title}</p>
                  <p className="ingest-plate__note">{blocked.note}</p>
                  <p className="ingest-plate__count">
                    {ready.toLocaleString()} points ready
                    {setAside > 0 && <> · {setAside.toLocaleString()} need attention</>}
                    {skippedSuffix}
                  </p>
                </>
              ) : (
                <>
                  <Icon icon={MapGlyph} size="xl" className="ingest-plate__glyph" />
                  <p className="ingest-plate__title">Your map will render here</p>
                  <p className="ingest-plate__instruction">
                    <Icon icon={TriangleAlert} size="sm" />
                    Help us map your data using the dropdowns on the right.
                  </p>
                </>
              )}
            </div>
            <span className="ingest-plate__stamp">
              {modeLabel} survey · {ready.toLocaleString()} points
            </span>
          </>
        )}
      </div>
    </Card>
  );
}
